import { useSyncExternalStore } from 'react';

import { createAiChatClient } from '@leagueplatform/ai';
import { ChatInstance, createChatInstance } from '@leagueplatform/chat';
import { chatInstance } from 'common/chat-instance';
import { transformRunRequest } from 'common/demo-selection';

// Which demo surfaces are open, and which chat instance the current demo runs on.
// A module-level store rather than a context: the menu item and the chat sit in
// different branches of the tree, and a provider around both would reset on every
// route change.
type DemoState = {
  isPickerOpen: boolean;
  isFullscreenChat: boolean;
  chat: ChatInstance;
  chatRunId: number;
};

let state: DemoState = {
  isPickerOpen: false,
  isFullscreenChat: false,
  chat: chatInstance,
  chatRunId: 0,
};
const listeners = new Set<() => void>();

const setState = (next: Partial<DemoState>) => {
  state = { ...state, ...next };
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => state;

export const openDemoPicker = () => setState({ isPickerOpen: true });
export const closeDemoPicker = () => setState({ isPickerOpen: false });

const closeDemoChat = (chat: ChatInstance) => {
  chat.actor.send({ type: 'SET_VISIBILITY', isOpen: false });
  setState({ isFullscreenChat: false });
};

// One action, unlike the SDK's own button: end the session and close the surface.
export const endDemoChat = () => {
  const { chat } = state;
  chat.actor.send({ type: 'USER_END_CHAT' });
  closeDemoChat(chat);
};

// A fresh instance per demo, keyed by `chatRunId`. Restarting one instance is not
// enough: the transcript renders past sessions, so the previous member's
// conversation would stay on screen, and a reopened session keeps answering as
// the selection it started with.
export const startDemoChat = () => {
  const previous = state.chat;
  const chat = createChatInstance({
    leagueClient: createAiChatClient({ transformRunRequest }),
  });

  setState({
    isPickerOpen: false,
    isFullscreenChat: true,
    chat,
    chatRunId: state.chatRunId + 1,
  });

  chat.actor.send({ type: 'USER_START_CHAT', initialClientKind: 'league' });
  chat.actor.send({ type: 'SET_VISIBILITY', isOpen: true });

  // An end from the agent's side finishes the demo too.
  const subscription = chat.actor.subscribe(() => {
    const snapshot = chat.actor.getSnapshot();
    if (!snapshot.matches('chatEnded') && !snapshot.matches('error')) return;
    subscription.unsubscribe();
    closeDemoChat(chat);
  });

  // After the re-render has unmounted it, and never the shared instance — the
  // /live-chat route still runs on that one.
  if (previous !== chatInstance) {
    setTimeout(() => previous.actor.stop(), 0);
  }
};

export const useDemoMode = (): DemoState =>
  useSyncExternalStore(subscribe, getSnapshot);
